/**
 * Hurka Browser-Client — Issues #1–#13.
 *
 *   #1  TypeScript + Babylon.js + npm workspaces
 *   #2  3D-Testwelt (Boden, Sky, mittelalterliche Gebäude, Outdoor-Licht)
 *   #3  First-Person-Steuerung (WASD, Maus, Pointer Lock, Kollision, Schwerkraft)
 *   #4  Retro-Pixel-Look (prozedurale Texturen, NEAREST, keine PBR)
 *   #5  Billboard-NPC (vertikal, immer zur Kamera, Pixel ohne Glättung)
 *   #6  WebSocket-Session + Connect/Disconnect + UUID + Status
 *   #7  Spielerposition-Sync (20 Hz broadcast, andere Spieler als Billboard)
 *   #8  Startmaske mit Name-Eingabe, serverseitig zugeordnet, über Kopf
 *   #9  Multiplayer-Chat (Eingabe, Broadcast, Historie)
 *   #10 Erster Gegner (Goblin) mit Patrouille + Aggro, server-authoritativ
 *   #11 Nahkampf (Linksklick, Cooldown, Reichweite, HP, Tod)
 *   #12 Loot-Drop (Sprite, E-Taste zur Aufnahme, Server entscheidet)
 *   #13 Inventar (Taste I, serverseitig, Name + Anzahl)
 *   #14 PostgreSQL-Persistenz — folgt separat
 */

import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import '@babylonjs/core/Cameras/Inputs/freeCameraKeyboardMoveInput';

import { buildSky } from './scene/sky';
import { buildScene, SPAWN_POSITION } from './scene/buildScene';
import { WORLD_LAYOUT } from './scene/nature';
import { buildBillboard } from './scene/npc';
import { npcSpriteTexture } from './scene/textures';
import { setupPointerLockControls } from './controls/pointerLockControls';
import { createMultiplayer, Multiplayer } from './net/multiplayer';
import { createNameMask } from './ui/nameMask';
import { createChatPanel } from './ui/chatPanel';
import { createInventoryPanel, InventoryPanelHandle } from './ui/inventoryPanel';
import { Minimap } from './ui/minimap';
import { EnemyRenderer, LootRenderer, PlayerRenderer } from './scene/entityRenderers';
import {
  HOUSE_DOOR_REACH_M,
  INTERIOR_DOOR_REACH_M,
  SHOWCASE_HOUSE,
  SHOWCASE_INTERIOR,
  type ChatLine,
  type EnemyState,
  type LootState,
  type PlayerState,
  type WorldMode,
} from '@hurka/shared';

// ---------------------------------------------------------------------------
// Engine + scene
// ---------------------------------------------------------------------------

const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
const engine = new Engine(canvas, true, {
  preserveDrawingBuffer: true,
  stencil: true,
});

const scene = new Scene(engine);
scene.clearColor = new Color4(0.5, 0.7, 1.0, 1);
scene.ambientColor = new Color3(0.4, 0.4, 0.45);
scene.fogMode = Scene.FOGMODE_LINEAR;
scene.fogColor = new Color3(0.7, 0.8, 0.9);
scene.fogStart = 50;
scene.fogEnd = 120;
scene.collisionsEnabled = true;
scene.gravity = new Vector3(0, -9.81, 0);

const camera = new UniversalCamera('cam', SPAWN_POSITION.clone(), scene);
camera.setTarget(new Vector3(0, 2, 0));
camera.checkCollisions = true;
camera.applyGravity = true;
camera.ellipsoid = new Vector3(0.5, 0.9, 0.5);
camera.ellipsoidOffset = new Vector3(0, 0.9, 0);

setupPointerLockControls(canvas, camera, scene);

const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
hemi.intensity = 0.55;
hemi.diffuse = new Color3(1.0, 0.97, 0.9);
hemi.groundColor = new Color3(0.35, 0.32, 0.28);
hemi.specular = new Color3(0, 0, 0);
const hemiOutdoorIntensity = 0.55;
const hemiInteriorIntensity = 0.18; // Issue #21 — interior is markedly darker

const sun = new DirectionalLight('sun', new Vector3(-0.4, -1, -0.3), scene);
sun.intensity = 1.1;
sun.diffuse = new Color3(1.0, 0.95, 0.82);
sun.specular = new Color3(0, 0, 0);
sun.position = new Vector3(40, 60, 30);

const shadowGen = new ShadowGenerator(1024, sun);
shadowGen.useBlurExponentialShadowMap = true;
shadowGen.blurKernel = 24;
shadowGen.depthScale = 50;

buildSky(scene);
const { showcase, nature } = buildScene(scene, shadowGen);

// Local test NPC billboard (Garron) — visual flavour, not networked.
const garron = buildBillboard(scene, {
  name: 'npc-garron',
  texture: npcSpriteTexture(scene),
  position: new Vector3(2, 1, 0),
  height: 2.0,
  alphaCutoff: 0.5,
});
garron.setLabel('Garron');

// ---------------------------------------------------------------------------
// UI panels
// ---------------------------------------------------------------------------

const chat = createChatPanel();
const inventory = createInventoryPanel() as InventoryPanelHandle;
let myInventory = new Map<string, number>();

// Local mode tracking (mirror of the server's authoritative mode).
// Default 'world' on first connect; updated by `house.entered` / `house.exited`.
type ClientMode = WorldMode;
let myMode: ClientMode = 'world';
let myInteriorId: string | null = null;

// ---------------------------------------------------------------------------
// Multiplayer wiring
// ---------------------------------------------------------------------------

const playerRenderer = new PlayerRenderer(scene);
const enemyRenderer = new EnemyRenderer(scene);
const lootRenderer = new LootRenderer(scene);

// HUD map (WoW-style round minimap + M-key full map).
const minimap = new Minimap(WORLD_LAYOUT);

let myId: string | null = null;
let myName: string | null = null;
let myLoot: LootState[] = [];
let myChatHistory: ChatLine[] = [];
// Latest world state, mirrored for the minimap renderer.  The minimap
// needs XZ coords + name (for other players), so we cache the raw
// PlayerState[] / EnemyState[] from the last world.state tick.
let myPlayers: PlayerState[] = [];
let myEnemies: EnemyState[] = [];

/** HUD hint element — set on first render. */
let interactHintEl: HTMLElement | null = null;

function setInteractHint(text: string | null): void {
  if (!interactHintEl) {
    interactHintEl = document.getElementById('hud-interact');
  }
  if (!interactHintEl) return;
  if (text === null) {
    interactHintEl.textContent = '';
    interactHintEl.classList.remove('visible');
  } else {
    interactHintEl.textContent = text;
    interactHintEl.classList.add('visible');
  }
}

let net: Multiplayer = createMultiplayer({
  onConnectionChange(state) {
    const el = document.getElementById('hud-connection');
    if (!el) return;
    el.textContent = state;
    el.classList.remove('connected', 'disconnected');
    if (state === 'connected') el.classList.add('connected');
    else if (state === 'disconnected') el.classList.add('disconnected');
  },
  onServerHello(msg) {
    myId = msg.playerId;
    chat.pushSystem(`connected as ${msg.playerId.slice(0, 8)}…`);
  },
  onWorldState(msg) {
    playerRenderer.apply(myId ?? '', msg.players);
    const enemy = msg.enemies[0] ?? null;
    enemyRenderer.apply(enemy);
    lootRenderer.apply(msg.loot);
    myLoot = msg.loot;
    // Cache for the minimap HUD.
    myPlayers = msg.players;
    myEnemies = msg.enemies;
  },
  onPlayerJoined(msg) {
    chat.pushSystem(`${msg.player.name ?? '???'} joined`);
  },
  onPlayerLeft(msg) {
    playerRenderer.remove(msg.playerId);
    chat.pushSystem(`player ${msg.playerId.slice(0, 8)} left`);
  },
  onChat(msg) {
    chat.pushLine(msg.line);
    myChatHistory.push(msg.line);
    if (myChatHistory.length > 50) myChatHistory.shift();
  },
  onLootSpawn(msg) {
    chat.pushSystem(`a chest appeared on the ground`);
  },
  onLootPickup(msg) {
    if (msg.playerId === myId) {
      chat.pushSystem('picked up a gold coin');
      // Optimistically bump local inventory until next world.state tick.
      const itemId = 'gold_coin';
      myInventory.set(itemId, (myInventory.get(itemId) ?? 0) + 1);
      inventory.setItems(myInventory);
    }
  },
  onEnemyDied(msg) {
    chat.pushSystem(`the goblin has been slain`);
  },
  onCombatDamage(msg) {
    // Could show damage numbers above enemy; keep it simple for now.
  },
  onHouseEntered(msg) {
    myMode = 'interior';
    myInteriorId = msg.interiorId;
    // Teleport the camera into the interior.  Snap to the server-given
    // spawn — we trust the server's authoritative coords.
    camera.position.set(msg.spawn.x, msg.spawn.y, msg.spawn.z);
    camera.rotation.y = msg.spawn.yaw;
    // Show the interior meshes, hide the door highlight.
    showcase.setInteriorVisible(true);
    showcase.setDoorHighlight('interior');
    // Dim the global hemispheric light so the warm interior actually
    // feels warmer than the cold exterior (Issue #21).
    hemi.intensity = hemiInteriorIntensity;
    // Re-enable interior lights (they're disabled when the player is
    // outside so they don't bleed through invisible walls).
    for (const l of showcase.interiorLights) {
      const anyL = l as unknown as { setEnabled?: (b: boolean) => void };
      anyL.setEnabled?.(true);
    }
    setInteractHint(null);
    chat.pushSystem(`entered ${msg.houseId}`);
  },
  onHouseExited(msg) {
    myMode = 'world';
    myInteriorId = null;
    camera.position.set(msg.spawn.x, msg.spawn.y, msg.spawn.z);
    camera.rotation.y = msg.spawn.yaw;
    showcase.setInteriorVisible(false);
    showcase.setDoorHighlight(null);
    hemi.intensity = hemiOutdoorIntensity;
    for (const l of showcase.interiorLights) {
      const anyL = l as unknown as { setEnabled?: (b: boolean) => void };
      anyL.setEnabled?.(false);
    }
    setInteractHint(null);
    chat.pushSystem(`stepped back outside`);
  },
});

chat.setOnSubmit((text) => {
  net.sendChat(text);
});

// ---------------------------------------------------------------------------
// Render loop
// ---------------------------------------------------------------------------

/**
 * Per-frame: compute door-reach state and update door highlight + HUD hint.
 *
 * Why per-frame and not in onBeforeRenderObservable?  We need the camera
 * position from Babylon's move step, which is updated by the time the
 * render observable fires.  Run after `scene.render()` so subsequent
 * ticks see the up-to-date state.
 */
function updateInteractPrompt(): void {
  if (myMode === 'interior') {
    const dx = camera.position.x - SHOWCASE_INTERIOR.exitX;
    const dz = camera.position.z - SHOWCASE_INTERIOR.exitZ;
    const inReach = dx * dx + dz * dz < 1.2 * 1.2;
    if (inReach) {
      showcase.setDoorHighlight('interior');
      setInteractHint('[E] Verlassen');
    } else {
      showcase.setDoorHighlight(null);
      setInteractHint(null);
    }
  } else {
    const dx = camera.position.x - SHOWCASE_HOUSE.doorX;
    const dz = camera.position.z - SHOWCASE_HOUSE.doorZ;
    const inReach = dx * dx + dz * dz < HOUSE_DOOR_REACH_M * HOUSE_DOOR_REACH_M;
    if (inReach) {
      showcase.setDoorHighlight('world');
      setInteractHint('[E] Betreten');
    } else {
      showcase.setDoorHighlight(null);
      setInteractHint(null);
    }
  }
}

engine.runRenderLoop(() => {
  const dt = engine.getDeltaTime() / 1000;
  showcase.update(dt);
  nature.update(dt); // pond UV scroll + phase swap
  updateInteractPrompt();

  // ---- Minimap HUD update ----
  // Push per-frame state from the local entities into the minimap and
  // trigger a redraw.  Cheaper than re-projecting the 3D scene.
  minimap.setState({
    player: {
      x: camera.position.x,
      z: camera.position.z,
      yaw: camera.rotation.y,
      name: myName ?? undefined,
    },
    players: myPlayers.map((p) => ({
      id: p.playerId,
      x: p.x,
      z: p.z,
      label: p.name ?? undefined,
    })),
    enemies: myEnemies.map((e) => ({
      id: e.enemyId,
      x: e.x,
      z: e.z,
      hp: e.hp,
    })),
    loot: myLoot.map((l) => ({ id: l.lootId, x: l.x, z: l.z })),
    inInterior: myMode === 'interior',
  });
  minimap.render();

  scene.render();
});
window.addEventListener('resize', () => engine.resize());

// ---------------------------------------------------------------------------
// Outbound position broadcast (20 Hz)
// ---------------------------------------------------------------------------

let lastMoveSent = 0;
scene.onBeforeRenderObservable.add(() => {
  const now = performance.now();
  if (now - lastMoveSent < 50) return; // 20 Hz
  lastMoveSent = now;
  const yaw = camera.rotation.y;
  net.sendMove(camera.position.x, camera.position.y, camera.position.z, yaw);
});

// ---------------------------------------------------------------------------
// Input:  attack (click), pickup (E), inventory (I), chat (Enter)
// ---------------------------------------------------------------------------

let lastAttackMs = 0;
const ATTACK_COOLDOWN = 800;

canvas.addEventListener('mousedown', (e) => {
  if (e.button !== 0) return;
  // Ignore clicks while pointer-locked (they should still work) — but the
  // attack only fires when NOT typing in chat/inventory.
  if (chat.isOpen() || inventory.isOpen()) return;
  const now = performance.now();
  if (now - lastAttackMs < ATTACK_COOLDOWN) return;
  lastAttackMs = now;
  // Attack the local enemy if it's the only one (Issue #10/11).
  net.sendAttack('enemy-1');
});

window.addEventListener('keydown', (e) => {
  const ke = e as KeyboardEvent;
  // Ignore input while typing in chat input.
  if (chat.isOpen()) return;

  if (ke.code === 'KeyI') {
    inventory.toggle();
  } else if (ke.code === 'KeyE') {
    // Context-dependent action:
    //   - In interior + near the exit door → leave the house.
    //   - In world + near the front door → enter the house.
    //   - Otherwise → try to pick up the nearest loot (Issue #12).
    if (myMode === 'interior') {
      const dx = camera.position.x - SHOWCASE_INTERIOR.exitX;
      const dz = camera.position.z - SHOWCASE_INTERIOR.exitZ;
      if (dx * dx + dz * dz < 1.2 * 1.2) {
        net.sendHouseExit();
        return;
      }
      // No door interaction; fall through to nothing.
      return;
    }

    // World mode: pick the closer action (door vs loot).
    const doorDx = camera.position.x - SHOWCASE_HOUSE.doorX;
    const doorDz = camera.position.z - SHOWCASE_HOUSE.doorZ;
    const doorD2 = doorDx * doorDx + doorDz * doorDz;
    const inDoorReach = doorD2 < HOUSE_DOOR_REACH_M * HOUSE_DOOR_REACH_M;

    let best: LootState | null = null;
    let bestD2 = Infinity;
    for (const l of myLoot) {
      if (l.claimedBy) continue;
      const dx = l.x - camera.position.x;
      const dz = l.z - camera.position.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = l;
      }
    }
    if (best && bestD2 < 2.5 * 2.5) {
      net.sendPickup(best.lootId);
      return;
    }
    if (inDoorReach) {
      net.sendHouseEnter(SHOWCASE_HOUSE.houseId);
    }
  }
});

// Open chat with Enter, unless inventory or chat is open.
window.addEventListener('keydown', (e) => {
  const ke = e as KeyboardEvent;
  // M toggles the full-map overlay.  Don't steal the key while typing
  // in the chat input or inventory panel.
  if (ke.key === 'm' || ke.key === 'M') {
    if (!chat.isOpen() && !inventory.isOpen()) {
      minimap.toggle();
      ke.preventDefault();
    }
    return;
  }
  if (ke.key === 'Escape') {
    // Escape also closes the full-map overlay before it closes the
    // inventory panel — feels like a natural priority order.
    const fullmap = document.querySelector('.fullmap-overlay.visible');
    if (fullmap) {
      minimap.toggle();
      ke.preventDefault();
      return;
    }
    if (inventory.isOpen()) inventory.close();
    return;
  }
  if (ke.key === 'Enter') {
    if (!chat.isOpen() && !inventory.isOpen()) {
      chat.open();
      ke.preventDefault();
    }
  }
});

// ---------------------------------------------------------------------------
// Boot: ask for name, then enter the world
// ---------------------------------------------------------------------------

const nameMask = createNameMask();
nameMask.waitForName().then((name) => {
  nameMask.hide();
  if (name) {
    myName = name;
    net.sendName(name);
    chat.pushSystem(`entered the realm as ${name}`);
  }
});

// Expose for debugging in dev.
(window as unknown as { __hurka: unknown }).__hurka = {
  scene,
  camera,
  net,
  chat,
  inventory,
};
